import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine
} from 'recharts';
import { supabase } from '../../../lib/supabase';
import { workersData, mockEvaluations, getUpdatedWorkersData } from '../../../mocks/workers';
import AdminAuthGuard from '../../../components/feature/AdminAuthGuard';

const RATING_CATEGORIES = [
  { key: "顧客目線_rating", label: "顧客目線" },
  { key: "笑顔_rating", label: "笑顔" },
  { key: "園の方針_rating", label: "園の方針" },
  { key: "事前準備_rating", label: "事前準備" },
  { key: "雰囲気_rating", label: "雰囲気" },
  { key: "運資質_rating", label: "運・資質" },
  { key: "清潔感_rating", label: "清潔感" },
  { key: "態度_rating", label: "態度" },
] as const;

interface RadarPreviewProps {
  workerId: string;
  workerName: string;
  averageScore: number;
  rank: string;
  anchorRef: React.RefObject<HTMLElement>;
  evaluations: any[];
}

function RadarPreview({ workerId, workerName, averageScore, rank, anchorRef, evaluations }: RadarPreviewProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const previewWidth = 300;
    const previewHeight = 300;
    const margin = 8;

    let left = rect.right + margin;
    let top = rect.top + rect.height / 2 - previewHeight / 2;

    if (left + previewWidth > window.innerWidth - 16) {
      left = rect.left - previewWidth - margin;
    }
    if (top < 8) top = 8;
    if (top + previewHeight > window.innerHeight - 8) {
      top = window.innerHeight - previewHeight - 8;
    }

    setPos({ top, left });
  }, [anchorRef]);

  const evals = evaluations.filter(e => e.worker_id === workerId);
  if (evals.length === 0) return null;

  const radarData = RATING_CATEGORIES.map(({ key, label }) => {
    const avg = evals.reduce((sum, ev) => sum + ((ev as any)[key] || 5), 0) / evals.length;
    return {
      category: label.length > 3 ? label.slice(0, 3) : label,
      fullLabel: label,
      value: avg,
      maxValue: 10,
    };
  });

  const getRankColor = (r: string) => {
    if (r === 'S') return 'text-amber-600 bg-amber-50 border-amber-200';
    if (r === 'A') return 'text-green-600 bg-green-50 border-green-200';
    if (r === 'B') return 'text-sky-600 bg-sky-50 border-sky-200';
    return 'text-gray-600 bg-gray-100 border-gray-200';
  };

  const getStrokeColor = (r: string) => {
    if (r === 'S') return '#f59e0b';
    if (r === 'A') return '#22c55e';
    if (r === 'B') return '#0ea5e9';
    return '#9ca3af';
  };

  if (!pos) return null;

  return (
    <div
      ref={previewRef}
      className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-none"
      style={{ top: pos.top, left: pos.left, width: 300 }}
    >
      {/* ヘッダー */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full">
            <span className="text-teal-700 font-bold text-sm">{workerName.charAt(0)}</span>
          </div>
          <span className="text-sm font-semibold text-gray-800">{workerName}</span>
        </div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-sm font-bold ${getRankColor(rank)}`}>
          {rank}ランク
        </div>
      </div>

      {/* レーダーチャート */}
      <div className="px-2 pt-1 pb-0">
        <ResponsiveContainer width="100%" height={200}>
          <RadarChart
            data={radarData}
            margin={{ top: 12, right: 20, bottom: 12, left: 20 }}
          >
            <PolarGrid stroke="#e5e7eb" gridType="polygon" />
            <PolarAngleAxis
              dataKey="category"
              tick={{ fill: "#6b7280", fontSize: 10 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 10]}
              tick={false}
              tickCount={6}
              axisLine={false}
            />
            <Tooltip
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow px-2 py-1 pointer-events-none">
                      <p className="text-sm font-medium text-gray-800">{d.fullLabel}</p>
                      <p className="text-base font-bold" style={{ color: getStrokeColor(rank) }}>{(d.value as number).toFixed(1)}点</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Radar
              name="最大値"
              dataKey="maxValue"
              stroke="#e5e7eb"
              fill="#f3f4f6"
              fillOpacity={0.4}
              strokeWidth={1}
              strokeDasharray="4 2"
            />
            <Radar
              name="スコア"
              dataKey="value"
              stroke={getStrokeColor(rank)}
              fill={getStrokeColor(rank)}
              fillOpacity={0.25}
              strokeWidth={2}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* フッター */}
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-sm text-gray-500">評価 {evals.length}件の平均</span>
        <span className="text-base font-bold text-gray-800">{averageScore.toFixed(1)}点 <span className="text-sm font-normal text-gray-400">/ 80点</span></span>
      </div>
    </div>
  );
}

// ホバー対応の詳細ボタン
function DetailButtonWithPreview({ worker }: { worker: Worker }) {
  const [hovered, setHovered] = useState(false);
  const btnRef = useRef<HTMLAnchorElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div className="relative inline-block">
      <Link
        ref={btnRef as React.RefObject<HTMLAnchorElement>}
        to={`/admin/workers/${worker.id}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <i className="ri-eye-line"></i>
        詳細
      </Link>
      {hovered && !isMobile && (
        <RadarPreview
          workerId={worker.id}
          workerName={worker.name}
          averageScore={worker.averageScore}
          rank={worker.rank}
          anchorRef={btnRef as React.RefObject<HTMLElement>}
          evaluations={worker.rawEvaluations}
        />
      )}
    </div>
  );
}

// 折れ線グラフホバープレビュー
interface TrendLinePreviewProps {
  worker: Worker;
  type: 'growth' | 'decline';
  anchorRef: React.RefObject<HTMLElement>;
}

function TrendLinePreview({ worker, type, anchorRef }: TrendLinePreviewProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [animatedData, setAnimatedData] = useState<{ date: string; score: number; displayScore: number }[]>([]);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const previewWidth = 260;
    const previewHeight = 200;
    const margin = 10;

    let left = rect.right + margin;
    let top = rect.top + rect.height / 2 - previewHeight / 2;

    if (left + previewWidth > window.innerWidth - 16) {
      left = rect.left - previewWidth - margin;
    }
    if (top < 8) top = 8;
    if (top + previewHeight > window.innerHeight - 8) {
      top = window.innerHeight - previewHeight - 8;
    }
    setPos({ top, left });
  }, [anchorRef]);

  // 評価履歴から折れ線データを生成
  const fullData = [...(worker.evaluationHistory || [])]
    .sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime())
    .map(h => {
      const score = (h.punctuality + h.teamwork + h.childCare + h.communication) * 2;
      const d = new Date(h.evaluatedAt);
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      return { date: label, score };
    });

  // アニメーション：ポイントを順番に描画
  useEffect(() => {
    if (fullData.length === 0) return;
    setAnimatedData([]);
    let idx = 0;
    const step = () => {
      idx++;
      setAnimatedData(fullData.slice(0, idx).map(d => ({ ...d, displayScore: d.score })));
      if (idx < fullData.length) {
        animFrameRef.current = window.setTimeout(step, 120);
      }
    };
    animFrameRef.current = window.setTimeout(step, 60);
    return () => {
      if (animFrameRef.current) clearTimeout(animFrameRef.current);
    };
  }, [worker.id]);

  if (!pos || fullData.length < 2) return null;

  const isGrowth = type === 'growth';
  const color = isGrowth ? '#22c55e' : '#ef4444';
  const bgFrom = isGrowth ? 'from-green-50' : 'from-red-50';
  const borderColor = isGrowth ? 'border-green-200' : 'border-red-200';
  const labelColor = isGrowth ? 'text-green-700' : 'text-red-700';
  const labelBg = isGrowth ? 'bg-green-100' : 'bg-red-100';
  const icon = isGrowth ? 'ri-arrow-up-line' : 'ri-arrow-down-line';
  const label = isGrowth ? '成長中' : '下降中';

  const scores = fullData.map(d => d.score);
  const minScore = Math.max(0, Math.min(...scores) - 4);
  const maxScore = Math.min(40, Math.max(...scores) + 4);

  const firstScore = fullData[0].score;
  const lastScore = fullData[fullData.length - 1].score;
  const diff = lastScore - firstScore;
  const diffLabel = diff >= 0 ? `+${diff}` : `${diff}`;

  return (
    <div
      className={`fixed z-[9999] bg-gradient-to-b ${bgFrom} to-white rounded-xl shadow-2xl border ${borderColor} overflow-hidden pointer-events-none`}
      style={{ top: pos.top, left: pos.left, width: 260 }}
    >
      {/* ヘッダー */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">{worker.name}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${labelBg} ${labelColor}`}>
            <i className={`${icon} text-xs`}></i>
            {label}
          </span>
        </div>
        <span className={`text-sm font-bold ${isGrowth ? 'text-green-600' : 'text-red-600'}`}>
          {diffLabel}pt
        </span>
      </div>

      {/* 折れ線グラフ */}
      <div className="px-2 pb-1">
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={animatedData} margin={{ top: 8, right: 12, bottom: 4, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: '#9ca3af', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[minScore, maxScore]}
              tick={{ fill: '#9ca3af', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <ReferenceLine
              y={firstScore}
              stroke={color}
              strokeDasharray="4 3"
              strokeOpacity={0.35}
              strokeWidth={1}
            />
            <Line
              type="monotone"
              dataKey="displayScore"
              stroke={color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: color, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 5, fill: color }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* フッター */}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">{fullData[0].date} → {fullData[fullData.length - 1].date}</span>
        <span className="text-xs text-gray-500">{fullData.length}回の評価</span>
      </div>
    </div>
  );
}

// 成長中・下降中バッジ（ホバープレビュー付き）
interface TrendBadgeProps {
  worker: Worker;
  type: 'growth' | 'decline';
}

function TrendBadge({ worker, type }: TrendBadgeProps) {
  const [hovered, setHovered] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const isGrowth = type === 'growth';
  const color = isGrowth
    ? { ping: 'bg-green-400', dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-100', icon: 'ri-arrow-up-line', label: '成長中' }
    : { ping: 'bg-red-400', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-100', icon: 'ri-arrow-down-line', label: '下降中' };

  return (
    <div
      className="relative inline-flex items-center gap-1 cursor-default"
      ref={badgeRef}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative">
        <div className={`absolute inset-0 ${color.ping} rounded-full animate-ping opacity-75`}></div>
        <div className={`relative w-4 h-4 flex items-center justify-center ${color.dot} rounded-full`}>
          <i className={`${color.icon} text-white text-xs animate-bounce`}></i>
        </div>
      </div>
      <span className={`text-xs ${color.bg} ${color.text} px-2 py-0.5 rounded-full font-medium animate-pulse`}>
        {color.label}
      </span>
      {hovered && !isMobile && (
        <TrendLinePreview
          worker={worker}
          type={type}
          anchorRef={badgeRef as React.RefObject<HTMLElement>}
        />
      )}
    </div>
  );
}

interface Worker {
  id: string;
  name: string;
  email: string;
  qualifications: string[] | null;
  created_at: string;
  averageScore: number;
  rank: string;
  evaluationHistory: any[];
  rawEvaluations: any[];
}

export default function AdminWorkersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [qualificationFilter, setQualificationFilter] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState('');

  const USE_MOCK_DATA = false;

  const refreshWorkers = () => {
    if (USE_MOCK_DATA) {
      setWorkers([...getUpdatedWorkersData()]);
    } else {
      fetchWorkers();
    }
  };

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setWorkers([...getUpdatedWorkersData()]);
      setLoading(false);
    } else {
      fetchWorkers();
    }
  }, []);

  // ルート変更（詳細ページから戻った時）に再取得
  useEffect(() => {
    refreshWorkers();
  }, [location.key]);

  // ウィンドウフォーカス・タブ表示切り替え時に再取得
  useEffect(() => {
    const handleFocus = () => refreshWorkers();
    const handleVisibilityChange = () => {
      if (!document.hidden) refreshWorkers();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchWorkers = async () => {
    try {
      setLoading(true);

      // ワーカー一覧を取得
      const { data: workersRaw, error: workersError } = await supabase
        .from('workers')
        .select('*')
        .order('created_at', { ascending: false });

      if (workersError) {
        console.error('Error fetching workers:', workersError);
        setLoading(false);
        return;
      }

      // 評価データを全件取得
      const { data: evaluationsRaw, error: evError } = await supabase
        .from('worker_evaluations')
        .select('*');

      if (evError) {
        console.error('Error fetching evaluations:', evError);
      }

      const evaluations = evaluationsRaw || [];

      const RATING_KEYS = [
        '顧客目線_rating', '笑顔_rating', '園の方針_rating', '事前準備_rating',
        '雰囲気_rating', '運資質_rating', '清潔感_rating', '態度_rating'
      ];

      const enriched: Worker[] = (workersRaw || []).map((w: any) => {
        const workerEvals = evaluations.filter((e: any) => e.worker_id === w.id);

        let averageScore = 0;
        let rank = '未評価';

        if (workerEvals.length > 0) {
          const categoryAverages = RATING_KEYS.map(key => {
            const sum = workerEvals.reduce((acc: number, ev: any) => acc + (ev[key] || 5), 0);
            return sum / workerEvals.length;
          });
          averageScore = categoryAverages.reduce((a, b) => a + b, 0);
          if (averageScore >= 60) rank = 'S';
          else if (averageScore >= 50) rank = 'A';
          else if (averageScore >= 40) rank = 'B';
          else rank = 'ブロック';
        }

        const evaluationHistory = workerEvals
          .sort((a: any, b: any) => new Date(b.work_date).getTime() - new Date(a.work_date).getTime())
          .map((ev: any) => ({
            evaluatedAt: ev.work_date,
            evaluatorName: ev.evaluator_name || '未記入',
            punctuality: ev.事前準備_rating || 3,
            teamwork: ev.雰囲気_rating || 3,
            childCare: ev.顧客目線_rating || 3,
            communication: ev.笑顔_rating || 3,
          }));

        return {
          ...w,
          averageScore,
          rank,
          evaluationHistory,
          rawEvaluations: workerEvals,
        };
      });

      setWorkers(enriched);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleDeleteWorker = async (workerId: string) => {
    setDeletingId(workerId);
    try {
      // 評価データを先に削除
      await supabase.from('worker_evaluations').delete().eq('worker_id', workerId);
      // オンボーディングデータを削除
      await supabase.from('worker_onboardings').delete().eq('worker_id', workerId);
      // ワーカー本体を削除
      const { error } = await supabase.from('workers').delete().eq('id', workerId);
      if (error) {
        console.error('Delete error:', error);
        alert('削除に失敗しました');
      } else {
        setWorkers(prev => prev.filter(w => w.id !== workerId));
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('削除中にエラーが発生しました');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
      setConfirmDeleteName('');
    }
  };

  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         worker.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesQualification = !qualificationFilter ||
                               (worker.qualifications && worker.qualifications.includes(qualificationFilter));
    const matchesRank = !rankFilter || worker.rank === rankFilter;
    return matchesSearch && matchesQualification && matchesRank;
  });

  const totalWorkers = workers.length;
  const qualifiedWorkers = workers.filter(w => w.qualifications && w.qualifications.length > 0).length;

  const availableQualifications = Array.from(
    new Set(
      workers
        .flatMap(worker => worker.qualifications || [])
        .filter(Boolean)
    )
  );

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'S': return 'text-amber-600 bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-200';
      case 'A': return 'text-green-600 bg-gradient-to-br from-green-50 to-emerald-100 border-green-200';
      case 'B': return 'text-sky-600 bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200';
      case '未評価': return 'text-gray-500 bg-gray-50 border-gray-200';
      case 'ブロック': return 'text-gray-600 bg-gray-100 border-gray-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getRankIcon = (rank: string) => {
    switch (rank) {
      case 'S': return 'ri-vip-crown-fill';
      case 'A': return 'ri-checkbox-circle-fill';
      case 'B': return 'ri-time-line';
      case '未評価': return 'ri-question-line';
      case 'ブロック': return 'ri-close-circle-fill';
      default: return 'ri-user-line';
    }
  };

  const getQualificationStyle = (qualification: string) => {
    if (qualification.includes('保育士')) {
      return { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-user-heart-line' };
    } else if (qualification.includes('看護師')) {
      return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'ri-stethoscope-line' };
    } else {
      return { bg: 'bg-teal-100', text: 'text-teal-700', icon: 'ri-award-line' };
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  };

  const isGrowthTrend = (worker: Worker) => {
    if (!worker.evaluationHistory || worker.evaluationHistory.length < 2) return false;
    const history = worker.evaluationHistory
      .sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime());
    if (history.length < 3) {
      const first = history[0].punctuality + history[0].teamwork + history[0].childCare + history[0].communication;
      const last = history[history.length - 1].punctuality + history[history.length - 1].teamwork + history[history.length - 1].childCare + history[history.length - 1].communication;
      return (last * 2) > (first * 2 + 4);
    }
    let improvementCount = 0;
    for (let i = 1; i < history.length; i++) {
      const prev = (history[i-1].punctuality + history[i-1].teamwork + history[i-1].childCare + history[i-1].communication) * 2;
      const current = (history[i].punctuality + history[i].teamwork + history[i].childCare + history[i].communication) * 2;
      if (current > prev) improvementCount++;
    }
    return improvementCount >= Math.ceil((history.length - 1) / 2);
  };

  const isDeclineTrend = (worker: Worker) => {
    if (!worker.evaluationHistory || worker.evaluationHistory.length < 2) return false;
    const history = worker.evaluationHistory
      .sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime());
    if (history.length < 3) {
      const first = history[0].punctuality + history[0].teamwork + history[0].childCare + history[0].communication;
      const last = history[history.length - 1].punctuality + history[history.length - 1].teamwork + history[history.length - 1].childCare + history[history.length - 1].communication;
      return (first * 2) > (last * 2 + 4);
    }
    let declineCount = 0;
    for (let i = 1; i < history.length; i++) {
      const prev = (history[i-1].punctuality + history[i-1].teamwork + history[i-1].childCare + history[i-1].communication) * 2;
      const current = (history[i].punctuality + history[i].teamwork + history[i].childCare + history[i].communication) * 2;
      if (current < prev) declineCount++;
    }
    return declineCount >= Math.ceil((history.length - 1) / 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 text-base">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-gray-50">
        {/* 削除確認モーダル */}
        {confirmDeleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                  <i className="ri-delete-bin-line text-red-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">ワーカーを削除</h3>
                  <p className="text-sm text-gray-500">この操作は取り消せません</p>
                </div>
              </div>
              <p className="text-base text-gray-700 mb-6">
                <span className="font-semibold text-gray-900">「{confirmDeleteName}」</span> さんのデータ（評価履歴を含む）をすべて削除しますか？
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setConfirmDeleteId(null); setConfirmDeleteName(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  キャンセル
                </button>
                <button
                  onClick={() => handleDeleteWorker(confirmDeleteId)}
                  disabled={deletingId === confirmDeleteId}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap text-sm"
                >
                  {deletingId === confirmDeleteId ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      削除中...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line"></i>
                      削除する
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ヘッダー */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-team-line text-teal-600 text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">ワーカー管理</h1>
                  <p className="text-sm text-gray-500 hidden sm:block">評価・スキル分析</p>
                </div>
                {/* 統計バッジ */}
                <div className="hidden sm:flex items-center gap-2 ml-2">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full">
                    <i className="ri-team-line text-teal-600 text-sm"></i>
                    <span className="text-sm text-teal-600 font-medium whitespace-nowrap">総ワーカー数</span>
                    <span className="text-base font-bold text-teal-700">{totalWorkers}</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                    <i className="ri-user-heart-line text-green-600 text-sm"></i>
                    <span className="text-sm text-green-600 font-medium whitespace-nowrap">保育士</span>
                    <span className="text-base font-bold text-green-700">
                      {workers.filter(w => w.qualifications && w.qualifications.some(q => q.includes('保育士'))).length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-full">
                    <i className="ri-stethoscope-line text-sky-600 text-sm"></i>
                    <span className="text-sm text-sky-600 font-medium whitespace-nowrap">看護師</span>
                    <span className="text-base font-bold text-sky-700">
                      {workers.filter(w => w.qualifications && w.qualifications.some(q => q.includes('看護師'))).length}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-4">
                <Link
                  to="/admin/reviews"
                  className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-chat-smile-2-line text-lg"></i>
                  <span className="text-sm hidden sm:inline">帰り道のアンケート</span>
                </Link>
                <Link
                  to="/mock-workers"
                  className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-test-tube-line text-lg"></i>
                  <span className="text-sm hidden sm:inline">モック一覧</span>
                </Link>
                <Link
                  to="/"
                  className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-home-line text-lg"></i>
                  <span className="text-sm hidden sm:inline">ホーム</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-logout-box-line text-lg"></i>
                  <span className="text-sm hidden sm:inline">ログアウト</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          {/* 検索・フィルター */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="名前・メールで検索"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <select
                value={qualificationFilter}
                onChange={(e) => setQualificationFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">全ての資格</option>
                {availableQualifications.map(qual => (
                  <option key={qual} value={qual}>{qual}</option>
                ))}
              </select>
              <select
                value={rankFilter}
                onChange={(e) => setRankFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="">全てのランク</option>
                <option value="S">Sランク</option>
                <option value="A">Aランク</option>
                <option value="B">Bランク</option>
                <option value="ブロック">ブロック</option>
              </select>
              <button
                onClick={() => { setSearchTerm(''); setQualificationFilter(''); setRankFilter(''); }}
                className="w-full px-4 py-2.5 bg-gray-100 text-gray-600 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap text-sm"
              >
                <i className="ri-refresh-line"></i>
                リセット
              </button>
            </div>
          </div>

          {/* ワーカー一覧 */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">ワーカー一覧</h3>
            </div>

            {filteredWorkers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                  <i className="ri-user-search-line text-gray-400 text-2xl sm:text-3xl"></i>
                </div>
                <p className="text-gray-500 mb-2 text-base sm:text-lg">該当するワーカーが見つかりません</p>
                <p className="text-gray-400 text-sm">検索条件を変更してお試しください</p>
              </div>
            ) : (
              <>
                {/* モバイル用カード表示 */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {filteredWorkers.map(worker => (
                    <div key={worker.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <Link
                        to={`/admin/workers/${worker.id}`}
                        className="block cursor-pointer"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                            <span className="text-teal-700 font-bold text-lg">
                              {worker.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="font-semibold text-gray-900 truncate text-base">{worker.name}</h4>
                              {isGrowthTrend(worker) && <TrendBadge worker={worker} type="growth" />}
                              {isDeclineTrend(worker) && <TrendBadge worker={worker} type="decline" />}
                            </div>
                            <p className="text-sm text-gray-500 truncate">{worker.email}</p>
                          </div>
                          <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${getRankColor(worker.rank)}`}>
                            <i className={`${getRankIcon(worker.rank)} text-sm`}></i>
                            <span className="font-bold text-sm">{worker.rank}</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1 mb-2">
                          {worker.qualifications && worker.qualifications.map((qual, idx) => {
                            const style = getQualificationStyle(qual);
                            return (
                              <span key={idx} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                                <i className={`${style.icon} text-xs`}></i>
                                {qual}
                              </span>
                            );
                          })}
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                          <span>評価数: {worker.evaluationHistory.length}件</span>
                          <span>平均: {worker.averageScore.toFixed(1)}点</span>
                          <span>{formatDate(worker.created_at)}</span>
                        </div>
                      </Link>
                      
                      {/* モバイル用アクションボタン */}
                      <div className="flex gap-2">
                        <Link
                          to={`/admin/workers/${worker.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-line"></i>
                          詳細
                        </Link>
                        {worker.rank !== 'B' && worker.rank !== '未評価' && (
                          <Link
                            to={`/admin/workers/${worker.id}/offer`}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-gift-line"></i>
                            オファー
                          </Link>
                        )}
                        <button
                          onClick={() => { setConfirmDeleteId(worker.id); setConfirmDeleteName(worker.name); }}
                          className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 hover:text-red-700 transition-colors cursor-pointer flex-shrink-0"
                          title="削除"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* デスクトップ用テーブル表示 */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          ワーカー
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          資格
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          ランク
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          評価
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          登録日
                        </th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">
                          アクション
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredWorkers.map(worker => (
                        <tr key={worker.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              <div className="w-11 h-11 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                                <span className="text-teal-700 font-bold text-base">
                                  {worker.name.charAt(0)}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="text-base font-semibold text-gray-900">{worker.name}</p>
                                  {isGrowthTrend(worker) && <TrendBadge worker={worker} type="growth" />}
                                  {isDeclineTrend(worker) && <TrendBadge worker={worker} type="decline" />}
                                </div>
                                <p className="text-sm text-gray-500 truncate">{worker.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {worker.qualifications && worker.qualifications.length > 0 ? (
                                worker.qualifications.map((qual, idx) => {
                                  const style = getQualificationStyle(qual);
                                  return (
                                    <span key={idx} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                                      <i className={`${style.icon} text-xs`}></i>
                                      {qual}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="text-sm text-gray-400">なし</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getRankColor(worker.rank)}`}>
                              <i className={`${getRankIcon(worker.rank)}`}></i>
                              <span className="font-bold text-sm">{worker.rank}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm">
                              <p className="font-semibold text-gray-900 text-base">{worker.averageScore.toFixed(1)}点</p>
                              <p className="text-sm text-gray-500">{worker.evaluationHistory.length}件</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-600">
                            {formatDate(worker.created_at)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-start gap-2">
                              <DetailButtonWithPreview worker={worker} />
                              {worker.rank !== 'B' && worker.rank !== '未評価' && (
                                <button
                                  onClick={() => navigate(`/admin/workers/${worker.id}/offer`)}
                                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white shadow-sm ${
                                    worker.rank === 'ブロック'
                                      ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                                      : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700'
                                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
                                >
                                  <i className={`ri-${worker.rank === 'ブロック' ? 'notification-2-line' : 'gift-line'} mr-1`}></i>
                                  {worker.rank === 'ブロック' ? '通知' : 'オファー'}
                                </button>
                              )}
                              <button
                                onClick={() => { setConfirmDeleteId(worker.id); setConfirmDeleteName(worker.name); }}
                                className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors cursor-pointer"
                                title="削除"
                              >
                                <i className="ri-delete-bin-line text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </AdminAuthGuard>
  );
}
